
import UserLoginWrapper from '@/components/auth-wrappers/UserLoginWrapper.vue'
import InputGroupDark from '@/components/layout/InputGroupDark.vue'
import DoubleScroll from '@/components/layout/DoubleScroll.vue'
import ProximityInfo from '@/components/layout/ProximityInfo.vue'
import AnswerDraftCheckBox from '@/components/inputs/AnswerDraftCheckBox.vue'
import AnswerPreview from '@/components/layout/AnswerPreview.vue'
import JrButton from '@/components/jr-parts/JrButton.vue'
import JrText from '@/components/jr-parts/JrText.vue'
import JrApplicantTitle from '@/components/jr-parts/JrApplicantTitle.vue'
import AnswerPrintExport from '@/components/actions/AnswerPrintExport.vue'
import { answerRule } from '@/constants/rules'

import { defineComponent, computed, ref, watch, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { RequestHeaderProximityForm } from '@/types/forms'
import Logger from '@/services/logger'
import { ElForm } from 'element-plus'
import { addMessageQueue, MESSAGE_TYPE_TOAST, MESSAGE_TYPE_ERROR } from '@/plugins/messageQueue'
import Message from '@/services/message'
import { useStore, Store } from '@/store'

interface AnswerForm {
  answer_draft_title: string | undefined | null
  answer_applicant_abbreviation: string | null
}

/**
 * 回答案作成
 */
export default defineComponent({
  name: 'ProximityAnswer',
  components: {
    InputGroupDark,
    DoubleScroll,
    UserLoginWrapper,
    ProximityInfo,
    AnswerDraftCheckBox,
    AnswerPreview,
    JrButton,
    JrText,
    JrApplicantTitle,
    AnswerPrintExport,
  },

  setup() {
    const store = useStore() as Store
    const route = useRoute()
    const router = useRouter()
    const formRef = ref<InstanceType<typeof ElForm>>()
    const storeAnswerItems = reactive<number[]>([])
    const requestHeaderId = computed(() => {
      return Number(route.params.request_header_id)
    })

    // 申請基本IDが正しくない場合404へ飛ばす
    if (Number.isNaN(requestHeaderId.value)) {
      Logger.info('申請基本IDが正しくない')
      router.push({ name: 'NotFound' })
    }

    const proximityInfo = ref<RequestHeaderProximityForm | null>(null)
    const form = reactive<AnswerForm>({
      answer_draft_title: '',
      answer_applicant_abbreviation: '',
    })
    const answerDraftItem = reactive<number[]>([])

    /**
     * 回答案候補でクリックされている全て回答項目情報を取得
     */
    const updateAnswerItem = (answerItem: number[]) => {
      answerDraftItem.splice(0)
      answerDraftItem.push(...answerItem)
    }

    /**
     * 回答項目複写
     */
    const copy = () => {
      if (answerDraftItem.length === 0) {
        // 対象が0件の場合はエラーメッセージを表示
        addMessageQueue(MESSAGE_TYPE_ERROR, Message.codes.NONE_COPY_TARGET)
        return
      }

      //storeに登録
      //別の配列に格納
      const tmpArray: number[] = [...answerDraftItem]
      store.commit('user/setAnswerDraftItems', tmpArray)
      addMessageQueue(MESSAGE_TYPE_TOAST, Message.bind(Message.codes.COPY_COMPLETE, { target: '回答項目' }))
    }

    /**
     * 回答項目貼り付け
     */
    const paste = () => {
      if (!store.getters['user/getAnswerDraftItems']) {
        addMessageQueue(MESSAGE_TYPE_ERROR, Message.codes.NONE_PASTE_TARGET)
        return
      }
      storeAnswerItems.splice(0)
      storeAnswerItems.push(...store.getters['user/getAnswerDraftItems'])
      updateAnswerItem(storeAnswerItems)
    }

    /**
     * 相手敬称の入力ボックスへの初期値設定
     */
    watch(
      () => proximityInfo,
      () => {
        if (proximityInfo.value == null || proximityInfo.value.proximity.answer_applicant_abbreviation == null) {
          form.answer_applicant_abbreviation = '貴■'
        } else {
          form.answer_applicant_abbreviation = proximityInfo.value.proximity.answer_applicant_abbreviation
        }
        if (proximityInfo.value?.proximity.answer_draft_title != null) {
          form.answer_draft_title = proximityInfo.value?.proximity.answer_draft_title
        }
      },
      { immediate: true, deep: true }
    )

    return {
      requestHeaderId,
      proximityInfo,
      form,
      answerDraftItem,
      answerRule,
      formRef,
      storeAnswerItems,
      updateAnswerItem,
      copy,
      paste,
    }
  },
})
